import React, { FunctionComponent } from "react";
import { MedigapPlan, MedigapPlanType } from "../utils/plans";
import { useTranslate } from "../../../helpers";
import PlanDetailsTable from "./PlanDetailsTable";
import { TooltipWithClose } from "../../../components";

interface PartAServicesProps {
  planType: MedigapPlanType;
  planData: MedigapPlan;
}

const PartAServices: FunctionComponent<PartAServicesProps> = ({
  planData,
  planType,
}) => {
  const t = useTranslate();

  const title = t("medigap.plan_details.part_a_what_you_will_pay");
  const data = [
    {
      property: t("medigap.plan_details.hospital_stays"),
      value: [
        {
          property: t("medigap.plan_details.hospital_stay_days_1_60"),
          value: t(planData.hospital_stay_1_60),
        },
        {
          property: t("medigap.plan_details.hospital_stay_days_61_90"),
          value: t(planData.hospital_stay_61_90),
        },
        {
          property: t("medigap.plan_details.hospital_stay_days_91_150"),
          value:
            // We need to check if the message has "lifetime reserve days" so we can add atooltip
            planData.hospital_stay_91_150 ===
            "medigap.ma_core.hospital_stay_91_150" ? (
              <>
                {t(planData.hospital_stay_91_150)}{" "}
                <TooltipWithClose
                  headerText={t(
                    "medigap.plan_details.lifetime_reserve_tooltip_title"
                  )}
                  title={t("mediap.plan_details.lifetime_reserve_tooltip")}
                >
                  {t("medigap.lifetime_reserve_days")}
                </TooltipWithClose>
              </>
            ) : (
              t(planData.hospital_stay_91_150)
            ),
        },
        planData.hospital_stay_after_150
          ? {
              property: t("medigap.plan_details.after_150"),
              value: t(planData.hospital_stay_after_150),
            }
          : null,
        planData.hospital_stay_additional_365
          ? {
              property: t("medigap.plan_details.hospital_stay_add_365_days"),
              value: t(planData.hospital_stay_additional_365),
            }
          : null,
        planData.hospital_stay_after_365
          ? {
              property: t("medigap.plan_details.hospital_stay_after_365_days"),
              value: t(planData.hospital_stay_after_365),
            }
          : null,
      ],
      tooltip: t("medigap.plan_details.hospital_tooltip"),
    },
    {
      property: t("medigap.plan_details.skilled_nursing_fac"),
      value: [
        {
          property: t("medigap.plan_details.snf_days_1_20"),
          value: t(planData.snf_days_1_20),
        },
        planData.snf_days_21_120
          ? {
              property: t("medigap.plan_details.snf_days_21_120"),
              value: t(planData.snf_days_21_120),
            }
          : null,
        planData.snf_days_21_100
          ? {
              property: t("medigap.plan_details.snf_days_21_100"),
              value: t(planData.snf_days_21_100),
            }
          : null,
        planData.snf_after_100_days
          ? {
              property: t("medigap.plan_details.snf_after_100_days"),
              value: t(planData.snf_after_100_days),
              hide:
                planData.state === "MA" &&
                [
                  MedigapPlanType.MA_SUPP_1,
                  MedigapPlanType.MA_SUPP_1A,
                ].includes(planType),
            }
          : null,
        planData.snf_after_120_days
          ? {
              property: t("medigap.plan_details.snf_after_120_days"),
              value: t(planData.snf_after_120_days),
            }
          : null,
        planData.snf_days_101_365
          ? {
              property: t("medigap.plan_details.snf_days_101_365"),
              value: t(planData.snf_days_101_365),
              hide:
                planData.state !== "MA" ||
                [MedigapPlanType.MA_CORE].includes(planType),
            }
          : null,
        planData.snf_days_after_365
          ? {
              property: t("medigap.plan_details.snf_days_after_365"),
              value: t(planData.snf_days_after_365),
              hide:
                planData.state !== "MA" ||
                [MedigapPlanType.MA_CORE].includes(planType),
            }
          : null,
      ],
    },
    {
      property: t("medigap.plan_details.snf_not_covered"),
      value: [
        planData.snf_not_days_1_30
          ? {
              property: t("medigap.plan_details.days_1_30"),
              value: t(planData.snf_not_days_1_30),
            }
          : null,
        planData.snf_not_after_30
          ? {
              property: t("medigap.plan_details.after_30"),
              value: t(planData.snf_not_after_30),
            }
          : null,
        planData.snf_not_covered_1_365
          ? {
              property: t("medigap.plan_details.snf_not_covered_1_365"),
              value: t(planData.snf_not_covered_1_365),
            }
          : null,
        planData.snf_not_covered_after_365_days
          ? {
              property: t(
                "medigap.plan_details.snf_not_covered_after_365_days"
              ),
              value: t(planData.snf_not_covered_after_365_days),
            }
          : null,
      ],
      hide: ![
        MedigapPlanType.MA_SUPP_1,
        MedigapPlanType.MA_SUPP_1A,
        MedigapPlanType.WI_HIGH_DEDUCTIBLE,
        MedigapPlanType.WI_25_COST,
        MedigapPlanType.WI_50_COST,
      ].includes(planType),
    },
    {
      property: t("medigap.plan_details.blood"),
      value: [
        {
          property: t("medigap.plan_details.blood_first_3"),
          value: t(planData.a_blood_first_3),
        },
        {
          property: t("medigap.plan_details.blood_after_3"),
          value: t(planData.a_blood_after_3),
        },
      ],
    },
    {
      property: t("medigap.plan_details.hospice"),
      value: t(planData.hospice),
    },
    {
      property: t("medigap.plan_details.mental_health_hospital_stays"),
      value: [
        planData.ip_mental_health_days_1_60
          ? {
              property: t("medigap.plan_details.hospital_stay_days_1_60"),
              value: t(planData.ip_mental_health_days_1_60),
            }
          : null,
        planData.ip_mental_health_days_61_90
          ? {
              property: t("medigap.plan_details.hospital_stay_days_61_90"),
              value: t(planData.ip_mental_health_days_61_90),
            }
          : null,
        planData.ip_mental_health_days_91_150
          ? {
              property: t("medigap.plan_details.hospital_stay_days_91_150"),
              value:
                planData.ip_mental_health_days_91_150 ===
                "medigap.ma_core.hospital_stay_91_150" ? (
                  <>
                    {t(planData.ip_mental_health_days_91_150)}{" "}
                    <TooltipWithClose
                      headerText={t(
                        "medigap.plan_details.lifetime_reserve_tooltip_title"
                      )}
                      title={t("mediap.plan_details.lifetime_reserve_tooltip")}
                    >
                      {t("medigap.lifetime_reserve_days")}
                    </TooltipWithClose>
                  </>
                ) : (
                  t(planData.ip_mental_health_days_91_150)
                ),
            }
          : null,
        planData.ip_mental_health_days_add_175
          ? {
              property: t("medigap.plan_details.additional_175"),
              value: t(planData.ip_mental_health_days_add_175),
            }
          : null,
        planData.ip_mental_health_days_after_175
          ? {
              property: t("medigap.plan_details.after_175"),
              value: t(planData.ip_mental_health_days_after_175),
            }
          : null,
      ],
      hide: planData.state !== "WI",
    },
    {
      property: t("medigap.plan_details.mental_health_stays_bio"),
      tooltip: t("medigap.plan_details.ma.mental_health_inpatient_bio_tooltip"),
      value: [
        planData.ip_mental_bio_days_1_60
          ? {
              property: t("medigap.plan_details.hospital_stay_days_1_60"),
              value: t(planData.ip_mental_bio_days_1_60),
            }
          : null,
        planData.ip_mental_bio_days_61_90
          ? {
              property: t("medigap.plan_details.hospital_stay_days_61_90"),
              value: t(planData.ip_mental_bio_days_61_90),
            }
          : null,
        planData.ip_mental_bio_days_91_190
          ? {
              property: t("medigap.plan_details.hospital_stay_days_91_150"),
              // We need to check if the message has "lifetime reserve days" so we can add atooltip
              value:
                planData.ip_mental_bio_days_91_190 ===
                "medigap.ma_core.hospital_stay_91_150" ? (
                  <>
                    {t(planData.ip_mental_bio_days_91_190)}{" "}
                    <TooltipWithClose
                      headerText={t(
                        "medigap.plan_details.lifetime_reserve_tooltip_title"
                      )}
                      title={t("mediap.plan_details.lifetime_reserve_tooltip")}
                    >
                      {t("medigap.lifetime_reserve_days")}
                    </TooltipWithClose>
                  </>
                ) : (
                  t(planData.ip_mental_bio_days_91_190)
                ),
            }
          : null,
        planData.ip_mental_bio_add_365_days
          ? {
              property: t("medigap.plan_details.hospital_stay_add_365_days"),
              value: t(planData.ip_mental_bio_add_365_days),
            }
          : null,
        planData.ip_mental_bio_beyond_add_365_days
          ? {
              property: t("medigap.plan_details.hospital_stay_after_365_days"),
              value: t(planData.ip_mental_bio_beyond_add_365_days),
            }
          : null,
      ],
      hide: planData.state !== "MA",
    },
    {
      property: t(
        "medigap.plan_details.ma.mental_health_inpatient_other_not_covered"
      ),
      value: [
        planData.ip_mental_other_first_60
          ? {
              property: t("medigap.plan_details.hospital_stay_days_1_60"),
              value: t(planData.ip_mental_other_first_60),
            }
          : null,
        planData.ip_mental_other_61
          ? {
              property: t(
                "medigap.plan_details.ma.mental_health_inpatient_other_not_covered.days_61_120"
              ),
              value: t(planData.ip_mental_other_61),
            }
          : null,
        planData.ip_mental_other_after_60_cy
          ? {
              property: t(
                "medigap.plan_details.ma.mental_health_inpatient_other_not_covered.after_120"
              ),
              value: t(planData.ip_mental_other_after_60_cy),
            }
          : null,
      ],
      hide: planData.state !== "MA",
    },
  ];

  const footerNotes: { text: string | JSX.Element; id: string }[] = [];

  return <PlanDetailsTable table={{ title, data, footerNotes }} />;
};

export default PartAServices;
